import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AdminLayout } from './components/admin/AdminLayout';
import { DashboardPage } from './pages/admin/DashboardPage';
import { ProductsPage } from './pages/admin/ProductsPage';
import { RacersManagementPage } from './pages/admin/RacersManagementPage';
import { UsersManagementPage } from './pages/admin/UsersManagementPage';
import { SettingsPage as AdminSettingsPage } from './pages/admin/SettingsPage';
import { TradingCardConfigPage } from './pages/admin/TradingCardConfigPage';
import { ProjectsPage } from './pages/admin/ProjectsPage';
import { PortfolioManagementPage } from './pages/admin/PortfolioManagementPage';
import { CartProvider } from './contexts/CartContext';
import { AuthProvider } from './contexts/AuthContext';
import { LoginPage } from './pages/LoginPage';
import { Layout } from './components/layout/Layout';
import ScrapingManagementPage from './pages/admin/ScrapingManagementPage';
import PitWallAdminPage from './pages/admin/PitWallAdminPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { Toaster } from 'react-hot-toast';
import { SnackbarProvider } from 'notistack';

// leave the React app for the site, keeping the page someone asked for
const ToSite: React.FC<{ hash: (p: Record<string, string | undefined>) => string }> = ({ hash }) => {
  const params = useParams();
  useEffect(() => { window.location.replace('/' + hash(params)); }, [hash, params]);
  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Fires a GA4 page_view on every client-side route change.
const RouteTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);
  return null;
};

const LayoutWrapper: React.FC = () => (
  <Layout>
    <div className="min-h-screen">
      <Outlet />
    </div>
  </Layout>
);

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <RouteTracker />
        <AuthProvider>
          <SnackbarProvider maxSnack={3}>
            <CartProvider>
              <Toaster position="top-right" />
              <Routes>
                {/* Auth Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<Navigate to="/" replace />} />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="projects" element={<ProjectsPage />} />
                  <Route path="portfolio" element={<PortfolioManagementPage />} />
                  <Route path="products" element={<ProductsPage />} />
                  <Route path="racers" element={<RacersManagementPage />} />
                  <Route path="trading-cards" element={<TradingCardConfigPage />} />
                  <Route path="scraping" element={<ScrapingManagementPage />} />
                  <Route path="pitwall" element={<PitWallAdminPage />} />
                  <Route path="users" element={<UsersManagementPage />} />
                  <Route path="settings" element={<AdminSettingsPage />} />
                </Route>

                {/* Old public pages now live on the site (frontend/public/site); send visitors there */}
                <Route path="/" element={<ToSite hash={() => ''} />} />
                <Route path="/events" element={<ToSite hash={() => '#results'} />} />
                <Route path="/events/:eventId" element={<ToSite hash={p => `#ev-${p.eventId}`} />} />
                <Route path="/racers/:racerId" element={<ToSite hash={p => `#racer-${p.racerId}`} />} />
                <Route path="/stats" element={<ToSite hash={() => '#stats'} />} />
                <Route path="*" element={<NotFoundPage />} />

                {/* Public Routes */}
                <Route element={<LayoutWrapper />}>
                  <Route path="/shop" element={<Navigate to="/" replace />} />
                  <Route path="/store" element={<Navigate to="/" replace />} />
                  <Route path="/cart" element={<Navigate to="/" replace />} />
                  <Route path="/checkout" element={<Navigate to="/" replace />} />
                  <Route path="/checkout/success" element={<Navigate to="/" replace />} />
                  <Route path="/checkout/cancel" element={<Navigate to="/" replace />} />
                  <Route path="/stats-dashboard" element={<Navigate to="/admin/pitwall" replace />} />
                  <Route path="/racers" element={<Navigate to="/" replace />} />
                  <Route path="/racers/stats" element={<Navigate to="/" replace />} />
                  <Route path="/settings" element={<Navigate to="/" replace />} />
                  <Route path="/profile" element={<Navigate to="/" replace />} />
                  
                  {/* Design Routes — redirected */}
                  <Route path="/design" element={<Navigate to="/" replace />} />
                  <Route path="/design/quote" element={<Navigate to="/" replace />} />
                  <Route path="/design/portfolio" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </CartProvider>
          </SnackbarProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
